import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateExternalAccount } from "@/features/external-accounts/hooks/use-external-accounts";
import type {
  ExternalAccount,
  ExternalAccountCapability,
} from "@/features/external-accounts/types";
import { useTranslation } from "@/features/utils/i18n";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface EditAccountDialogProps {
  account: ExternalAccount | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditAccountDialog = ({
  account,
  open,
  onOpenChange,
}: EditAccountDialogProps) => {
  const { t } = useTranslation("externalAccounts");
  const [name, setName] = useState("");
  const [capabilities, setCapabilities] = useState<ExternalAccountCapability[]>(
    [],
  );

  const updateAccount = useUpdateExternalAccount();

  useEffect(() => {
    if (account) {
      setName(account.name);
      setCapabilities(account.capabilities?.capabilities || []);
    }
  }, [account]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!account) return;

    try {
      await updateAccount.mutateAsync({
        id: account.id,
        data: {
          name,
          capabilities: { capabilities },
        },
      });

      toast.success(t("externalAccounts.messages.accountUpdated"));
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("externalAccounts.messages.updateError"),
      );
    }
  };

  if (!account) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("externalAccounts.editDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("externalAccounts.editDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">
              {t("externalAccounts.editDialog.accountName")}
            </Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("externalAccounts.buttons.cancel")}
            </Button>
            <Button type="submit">
              {t("externalAccounts.buttons.saveChanges")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
